import React, { useContext } from 'react'

import { RouterPaths } from '../router/RouterPathsMapper'

export interface ExitNavigationOptions {
  targetRoute?: string
  replace?: boolean
}

export type ExitHandler = (options?: ExitNavigationOptions) => void | Promise<void>

interface ChangeTabContextProps {
  tabToWatch: RouterPaths
  discardRoute: string | null
  isWatching: boolean
  onDiscardAndExit: ExitHandler | null
  onSaveAndExit: ExitHandler | null
  setTabToWatch: (tab: RouterPaths) => void
  setDiscardRoute: (discardRoute: string | null) => void
  setOnDiscardAndExit: (handler: ExitHandler | null) => void
  setOnSaveAndExit: (handler: ExitHandler | null) => void
  setIsWatching?: (isWatching: boolean) => void
}

export const ChangeTabContext = React.createContext<ChangeTabContextProps>(null)

interface ChangeTabProviderProps {
  children: React.ReactNode
}

export const ChangeTabProvider: React.FC<ChangeTabProviderProps> = ({ children }) => {
  const [currentTab, setCurrentTab] = React.useState<RouterPaths>(null)
  const [discardRoute, setDiscardRoute] = React.useState<string | null>(null)
  const [watching, setWatching] = React.useState<boolean>(false)
  const [onDiscardAndExit, setOnDiscardAndExit] = React.useState<ExitHandler | null>(null)
  const [onSaveAndExit, setOnSaveAndExit] = React.useState<ExitHandler | null>(null)

  const handleSetOnDiscardAndExit = React.useCallback((handler: ExitHandler | null) => {
    setOnDiscardAndExit(handler ? () => handler : null)
  }, [])

  const handleSetOnSaveAndExit = React.useCallback((handler: ExitHandler | null) => {
    setOnSaveAndExit(handler ? () => handler : null)
  }, [])

  return (
    <ChangeTabContext.Provider
      value={{
        tabToWatch: currentTab,
        discardRoute,
        isWatching: watching,
        onDiscardAndExit,
        onSaveAndExit,
        setTabToWatch: setCurrentTab,
        setDiscardRoute,
        setOnDiscardAndExit: handleSetOnDiscardAndExit,
        setOnSaveAndExit: handleSetOnSaveAndExit,
        setIsWatching: setWatching
      }}
    >
      {children}
    </ChangeTabContext.Provider>
  )
}

interface ChangeTabWatcherOptions {
  discardRoute?: string
  onDiscardAndExit?: ExitHandler
  onSaveAndExit?: ExitHandler
}

export function useChangeTabWatcher(
  tabToWatch?: RouterPaths,
  disabled?: boolean,
  discardRouteOrOptions?: string | ChangeTabWatcherOptions
) {
  const changeTabWatcher = useContext(ChangeTabContext)
  const options =
    typeof discardRouteOrOptions === 'string' ? { discardRoute: discardRouteOrOptions } : discardRouteOrOptions

  const discardRoute = options?.discardRoute || null

  // Callers pass the handlers inline, so they get a fresh identity on every
  // render. Reading them through refs lets the effect below register stable
  // wrappers and depend only on primitive values — otherwise the effect would
  // re-run every render and, since it writes to this provider's state, spin
  // into an infinite update loop ("Maximum update depth exceeded").
  const onDiscardAndExitRef = React.useRef(options?.onDiscardAndExit)
  const onSaveAndExitRef = React.useRef(options?.onSaveAndExit)
  onDiscardAndExitRef.current = options?.onDiscardAndExit
  onSaveAndExitRef.current = options?.onSaveAndExit

  React.useEffect(() => {
    if (!tabToWatch || disabled) return
    changeTabWatcher.setTabToWatch(tabToWatch)
    changeTabWatcher.setDiscardRoute(discardRoute)
    changeTabWatcher.setOnDiscardAndExit(
      onDiscardAndExitRef.current ? (exitOptions) => onDiscardAndExitRef.current?.(exitOptions) : null
    )
    changeTabWatcher.setOnSaveAndExit(
      onSaveAndExitRef.current ? (exitOptions) => onSaveAndExitRef.current?.(exitOptions) : null
    )
    changeTabWatcher.setIsWatching(true)

    return () => {
      changeTabWatcher.setTabToWatch(null)
      changeTabWatcher.setDiscardRoute(null)
      changeTabWatcher.setOnDiscardAndExit(null)
      changeTabWatcher.setOnSaveAndExit(null)
      changeTabWatcher.setIsWatching(false)
    }
  }, [tabToWatch, disabled, discardRoute])

  return {
    tabToWatch: changeTabWatcher.tabToWatch,
    discardRoute: changeTabWatcher.discardRoute,
    isWatching: changeTabWatcher.isWatching,
    onDiscardAndExit: changeTabWatcher.onDiscardAndExit,
    onSaveAndExit: changeTabWatcher.onSaveAndExit
  }
}
